import {Paragraph} from "@/pages/common/Home/Dissection/Article";

import benefitsMob from "@images/home/analytics/ai_in_action/article_1-benefits-mob.png";
import benefits from "@images/home/analytics/ai_in_action/article_1-benefits.png";
import collage from "@images/home/analytics/ai_in_action/article_1-collage.png";
import knowledge_1 from "@images/home/analytics/ai_in_action/article_1-knowledge_1.png";
import knowledge_2 from "@images/home/analytics/ai_in_action/article_1-knowledge_2.png";
import benefits_blob from "@images/home/analytics/ai_in_action/article_1_benefits-blob.png";
import key_features from "@images/home/analytics/ai_in_action/article_1_key_features.png";
import pilot_blob from "@images/home/analytics/ai_in_action/article_1_pilot-blob.png";
import pilot from "@images/home/analytics/ai_in_action/article_1_pilot.png";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {H2} from "@ui/titles/H2";
import {motion} from "framer-motion";
import {useSelector} from "react-redux";

import styles from "../styles.module.scss";

const itemAnimation = {
  hidden: {
    opacity: 0,
    y: 100,
  },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: custom * 0.3,
    },
  }),
};

export const ArticleAiInAction = () => {
  const {isMobile} = useSelector((state) => state.common.settings);

  return (
    <>
      <Paragraph>
        <H2>
          <span>
            AI&nbsp;in&nbsp;Action: Driving Sales Force <br />
            Performance and Effectiveness
          </span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              The pharmaceutical industry is&nbsp;fiercely competitive and
              regulated, and the success of&nbsp;a&nbsp;medical representative
              (MR) hinges not just on&nbsp;building relationships, but
              on&nbsp;effectively and accurately delivering key messages and
              overcoming objections to&nbsp;drive sales.
            </span>

            <span>
              Decision-makers, particularly in&nbsp;Commercial Excellence, are
              laser-focused on&nbsp;outcomes because Sales Force is&nbsp;the
              most expensive resource that companies invest
              in&nbsp;to&nbsp;promote their products. When medical
              representatives struggle to&nbsp;deliver product information
              effectively or&nbsp;address concerns during visits,
              it&nbsp;directly impacts sales performance and revenue. This
              is&nbsp;why there is&nbsp;such a&nbsp;strong emphasis on&nbsp;the
              effectiveness of&nbsp;the company’s&nbsp;use of&nbsp;Sales Force;
              ensuring that MRs are well-prepared and capable is&nbsp;crucial
              to&nbsp;maximising the return on&nbsp;this significant investment.
            </span>

            <span>
              Every medical representative has faced various challenges, from
              navigating difficult and lengthy medical marketing information
              that&rsquo;s hard to&nbsp;remember, to&nbsp;engaging with
              non-responsive healthcare professionals (HCPs) who seem like
              impenetrable walls. Despite having important information to share,
              attempts to&nbsp;engage often seem to&nbsp;fall on&nbsp;deaf ears,
              met with hurried nods or&nbsp;distracted glances.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Introducing COSMART AVA</span>
        </H2>

        <div className={styles.article_text_image}>
          <RootDesc>
            <span>
              This common scenario is&nbsp;precisely what AVA was designed to
              address. AVA is&nbsp;one of&nbsp;the flagship solutions that
              is&nbsp;part of&nbsp;COSMART&nbsp;&mdash; a&nbsp;comprehensive
              suite of&nbsp;AI-powered tools developed by&nbsp;Vivanti, designed
              to&nbsp;drive commercial excellence. COSMART encompasses
              a&nbsp;variety of&nbsp;solutions aimed at&nbsp;enhancing
              commercial operations, from analytics and market insights
              to&nbsp;customer relationship management, beyond just training
              medical representatives.
            </span>

            <span>
              As&nbsp;a&nbsp;cutting-edge AI&nbsp;training simulator, AVA equips
              medical representatives to&nbsp;effectively engage with busy and
              sometimes indifferent HCPs. It&nbsp;provides MRs with the latest
              product knowledge, effective objection-handling strategies, and
              essential messaging techniques. The measurable outcomes from
              AVA’s&nbsp;simulations not only identify areas needing improvement
              but also support targeted efforts to&nbsp;boost team performance
              and drive sales success. Through COSMART, Vivanti offers
              a&nbsp;robust platform that significantly enhances commercial
              effectiveness across the board.
            </span>
          </RootDesc>

          <img
            style={{width: isMobile ? "100%" : "625rem"}}
            src={collage}
            alt={"image"}
          />
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>The Critical Role of&nbsp;AVA in&nbsp;Sales Performance</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              For Senior Managers in&nbsp;Commercial Excellence, the priority is
              clear: driving sales performance. While the confidence of&nbsp;MRs
              is&nbsp;important, it&nbsp;is&nbsp;their adherence
              to&nbsp;strategy, ability to handle objections, and capacity
              to&nbsp;answer a&nbsp;wide range of product-related questions that
              truly determines success. Strong communication skills and sales
              techniques are essential for the sales force
              to&nbsp;be&nbsp;effective, particularly in&nbsp;the high-pressure
              environment of&nbsp;medical sales.
            </span>

            <span>
              Given the evolving landscape of&nbsp;healthcare in&nbsp;the UK,
              including the increasing pressures on&nbsp;doctors, suggests that
              their availability for meetings with MRs may be&nbsp;limited.
              Therefore, MRs must be&nbsp;able to&nbsp;quickly and confidently
              present the most relevant information about the drug, persuading
              doctors to&nbsp;choose it. However, many MRs struggle
              to&nbsp;recall and utilise key marketing materials during these
              critical moments, which can hinder their effectiveness
              in&nbsp;conveying the essential information.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>AVA: The Solution to&nbsp;Marketing Challenges</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              This is&nbsp;where AVA becomes an&nbsp;indispensable tool.
              It&nbsp;provides an&nbsp;efficient, seamless process for training
              and knowledge assessment, ensuring that the team
              is&nbsp;well-prepared for real-life interactions and capable
              of&nbsp;accurately delivering the company&rsquo;s strategy.
              AVA’s&nbsp;platform allows for the rapid upload of&nbsp;training
              materials and provides access to simulations that MRs can engage
              with, which is&nbsp;crucial for quickly responding to&nbsp;market
              changes (such as&nbsp;the entry of&nbsp;new competitors)
              or&nbsp;addressing urgent issues like drug recalls that need
              to&nbsp;be&nbsp;discussed during visits.
            </span>

            <span>
              With AVA, MRs not only master product knowledge but also gain the
              confidence to&nbsp;handle objections, convey key messages, and
              make the most of&nbsp;their limited time with doctors, ultimately
              driving stronger sales outcomes. This tool ensures that MRs are
              fully equipped to&nbsp;meet the demands of&nbsp;their role,
              translating into improved performance and a&nbsp;direct positive
              impact on&nbsp;the bottom line.
            </span>

            <span>
              Research on&nbsp;knowledge retention indicates that
              a&nbsp;significant amount of&nbsp;information is&nbsp;forgotten
              shortly after training or learning experiences. Here are some key
              statistics:
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>AVA: The KEY to&nbsp;Sales Force Knowledge Retention</span>
        </H2>

        <div className={styles.article_subtitle_image}>
          <RootDesc>
            <span>1. Forgetting Curve:[2],[5]</span>
          </RootDesc>

          <img
            src={knowledge_1}
            alt={"image"}
          />
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_subtitle_image}>
          <RootDesc>
            <span>
              2. Retention Rates by&nbsp;Method: The effectiveness
              of&nbsp;different learning methods varies significantly:[3]
            </span>
          </RootDesc>

          <img
            src={knowledge_2}
            alt={"image"}
          />
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_subtitle_image}>
          <RootDesc>
            <span>
              3. Reinforcement Importance: Without reinforcement and active
              engagement, most of&nbsp;the learned material fades quickly.
              Techniques like spaced repetition and practical application can
              dramatically improve long-term retention[1][4].
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_subtitle_image}>
          <RootDesc>
            <span>
              These statistics underscore the challenge of&nbsp;retaining
              information without active use or&nbsp;reinforcement, highlighting
              the need for effective training strategies that promote long-term
              knowledge retention.
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Key Features of&nbsp;AVA</span>
        </H2>

        <div className={styles.article_key_features_bg}>
          <ul>
            <li>
              <div>
                Pharma-Driven <br />
                Training Library:
              </div>

              <p>
                Ensures that simulations aligns with the
                company’s&nbsp;protocols and product knowledge, leading
                to&nbsp;more consistent messaging and strategy execution across
                the team.
              </p>
            </li>

            <li>
              <div>Customisable Training Scenarios:</div>

              <p>
                Managers can tailor scenarios to&nbsp;target specific training
                needs, as&nbsp;well as&nbsp;levels
                of&nbsp;difficulty&nbsp;&mdash; ensuring that the simulation
                is&nbsp;relevant and effective for their team’s development.
              </p>
            </li>

            <li>
              <div>Performance Transparency:</div>

              <p>
                AVA’s&nbsp;robust analytics provide Commercial Excellence
                Managers with real-time insights into MR&nbsp;performance,
                enabling data-driven decisions to&nbsp;enhance training and
                field effectiveness.
              </p>
            </li>

            <li>
              <div>Product Portfolio Management:</div>

              <p>
                Allows managers to&nbsp;keep field force materials up-to-date
                with the latest product information, ensuring MRs are always
                prepared with accurate details.
              </p>
            </li>
          </ul>

          <h4>
            Benefits for <br />
            Commercial <br />
            Excellence <br />
            Managers:
          </h4>

          <img
            src={key_features}
            alt={"image"}
          />
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_subtitle_image}>
          <H2>
            <span>Benefits for Medical Representatives (MRs):</span>
          </H2>

          <div className={styles.article_benefits}>
            {isMobile && (
              <img
                style={{maxWidth: "300rem"}}
                className={styles.article_benefits_blob}
                src={benefits_blob}
                alt={"blob"}
              />
            )}

            <ul>
              <li>
                <RootDesc>
                  <b>Impactful Communication Feedback:</b>
                </RootDesc>

                <p>
                  Immediate, actionable feedback on&nbsp;communication
                  techniques empowers MRs to&nbsp;refine their approach and
                  improve their persuasive skills.
                </p>
              </li>

              <li>
                <RootDesc>
                  <b>Emotional Intelligence Training:</b>
                </RootDesc>

                <p>
                  Developing emotional intelligence helps MRs improve their
                  interactions with healthcare professionals, leading to more
                  successful engagements.
                </p>
              </li>

              <li>
                <RootDesc>
                  <b>Customisable Scenarios: </b>
                </RootDesc>

                <p>
                  Tailored training scenarios allow MRs to&nbsp;practice in
                  situations that are directly relevant to&nbsp;their specific
                  field, making the training highly applicable and effective.
                </p>
              </li>

              <li>
                <RootDesc>
                  <b>Dynamic Interaction:</b>
                </RootDesc>

                <p>
                  AVA&rsquo;s ability to&nbsp;adjust its behaviour based
                  on&nbsp;MR performance creates an&nbsp;engaging, real-world
                  training environment that enhances learning.
                </p>
              </li>
            </ul>

            <img
              style={isMobile ? {marginRight: "unset"} : {}}
              className={styles.article_benefits_men}
              src={isMobile ? benefitsMob : benefits}
              alt={"image"}
            />
          </div>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_subtitle_image}>
          <H2>
            <span>Impactful Results from Pilot</span>
          </H2>

          <RootDesc>
            <span>
              The effectiveness of&nbsp;AVA is&nbsp;underscored
              by&nbsp;impressive results conducted by&nbsp;Vivanti:
            </span>
          </RootDesc>

          <div className={styles.article_pilot}>
            <img
              style={isMobile ? {maxWidth: "270rem"} : {}}
              className={styles.article_pilot_blob}
              src={pilot_blob}
              alt={"blob"}
            />

            {!isMobile && (
              <img
                className={styles.article_pilot_girl}
                src={pilot}
                alt={"image"}
              />
            )}

            <motion.ul
              initial={"hidden"}
              whileInView={"visible"}
              viewport={{amount: 0.5, once: true}}
            >
              <motion.li
                custom={1}
                variants={itemAnimation}
              >
                <RootDesc>
                  <b>
                    Memorisation of&nbsp;Visit <br />
                    Scenarios:
                  </b>
                  <br />
                  <span>MRs demonstrated a</span> <br />
                  <strong>58%</strong> <br />
                  <span>
                    improvement in&nbsp;their ability <br />
                    to&nbsp;remember details of&nbsp;their visit <br />
                    scenarios.
                  </span>
                </RootDesc>
              </motion.li>

              <motion.li
                custom={2}
                variants={itemAnimation}
              >
                <RootDesc>
                  <b>
                    Enhanced Product <br /> Knowledge:
                  </b>
                  <br />
                  <span>There was a&nbsp;</span> <strong>60%</strong> <br />
                  <span>
                    increase in&nbsp;MRs&rsquo; product knowledge, <br />
                    ensuring they are <nobr>well-prepared</nobr> <br />
                    to&nbsp;discuss and promote their products <br />
                    effectively.
                  </span>
                </RootDesc>
              </motion.li>

              <motion.li
                custom={3}
                variants={itemAnimation}
              >
                <RootDesc>
                  <b>
                    Improved <br /> Communication Skills:
                  </b>
                  <br />
                  <span>MRs experienced a</span> <br />
                  <strong>65%</strong> <br />
                  <span>
                    improvement in&nbsp;communication <br />
                    skills, enabling them to&nbsp;engage <br />
                    more persuasively <br />
                    and confidently with HCPs.
                  </span>
                </RootDesc>
              </motion.li>
            </motion.ul>
          </div>
        </div>
      </Paragraph>

      <Paragraph>
        <H2>
          <span>Market Debut and Seeking Early Adopters</span>
        </H2>

        <div className={styles.article_subtitle}>
          <RootDesc>
            <span>
              As&nbsp;COSMART AVA makes its market debut at&nbsp;the 14th Annual
              Sales Force Effectiveness and Digital Marketing in&nbsp;Pharma
              Summit in&nbsp;Berlin on&nbsp;16th October 2024&nbsp;&mdash; where
              the team will be showcasing the solution to&nbsp;industry leaders.
              Vivanti is welcoming early adopters who wish to&nbsp;elevate their
              medical representatives&rsquo; training programmes. This
              opportunity allows industry leaders to&nbsp;integrate cutting-edge
              technology into their training regimen and set a&nbsp;new standard
              in&nbsp;MR preparedness within the COSMART framework.
            </span>

            <span>
              Marina Hickson, Managing Director of&nbsp;Vivanti comments,
              &laquo;As healthcare evolves, clear communication of&nbsp;complex
              medical information is&nbsp;more vital than ever. AVA, integrated
              with our COSMART suite, revolutionizes medical sales training by
              fostering meaningful interactions. It&nbsp;equips reps with both
              product knowledge and emotional intelligence to&nbsp;build
              insightful connections with healthcare professionals, leading
              to&nbsp;better outcomes. With AVA, we&rsquo;re not just upgrading
              a tool-we&rsquo;re transforming reps into trusted partners
              in&nbsp;healthcare, shaping the future of&nbsp;patient
              care.&raquo;
            </span>
          </RootDesc>
        </div>
      </Paragraph>

      <Paragraph>
        <div className={styles.article_citations}>
          <h6>Citations:</h6>

          <RootDesc>
            <ol>
              <li>
                <a
                  href={"https://intellek.io/blog/knowledge-retention/"}
                  target={"_blank"}
                >
                  https://intellek.io/blog/knowledge-retention/
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://www.bizlibrary.com/blog/learning-methods/learning-retention-key-employee-training/"
                  }
                  target={"_blank"}
                >
                  https://www.bizlibrary.com/blog/learning-methods/learning-retention-key-employee-training/
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://www.educate-me.co/blog/knowledge-retention-strategies"
                  }
                  target={"_blank"}
                >
                  https://www.educate-me.co/blog/knowledge-retention-strategies
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://knowledgeanywhere.com/articles/seventy-percent-of-your-training-is-forgotten-learn-the-science-of-knowledge-retention-and-how-it-affects-online-training/"
                  }
                  target={"_blank"}
                >
                  https://knowledgeanywhere.com/articles/seventy-percent-of-your-training-is-forgotten-learn-the-science-of-knowledge-retention-and-how-it-affects-online-training/
                </a>
              </li>

              <li>
                <a
                  href={
                    "https://www.indegene.com/what-we-think/reports/understanding-science-behind-learning-retention"
                  }
                  target={"_blank"}
                >
                  https://www.indegene.com/what-we-think/reports/understanding-science-behind-learning-retention
                </a>
              </li>
            </ol>
          </RootDesc>
        </div>
      </Paragraph>
    </>
  );
};
